// PURE layout maths (IMPLEMENTATION §2.6). The only source of board ↔ client coordinate mapping.
// M0: Regular + Compact classes, bottom-aligned board, centred 480 column. M1 extends the test table.
// M5: the board is the current expansion mask (cols × rows) whose top-left WORLD cell is `origin`; every
// cell coordinate in and out of this module is a world cell on the fixed 7×9 grid (§ Conventions).

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Cell {
  x: number;
  y: number;
}

export type LayoutClass = 'regular' | 'compact';

export interface ClassConfig {
  minHs: number;
  hud: number;
  chip: number;
  gapTop: number;
  gapBottom: number;
  counter: number;
  tabs: number;
}

/** Mirrors `ui.json.layout`. */
export interface LayoutConfig {
  maxWidth: number;
  gutter: number;
  frame: number;
  cellMax: number;
  cellMin: number;
  cellFloor: number;
  resizeDebounceMs: number;
  classes: Record<LayoutClass, ClassConfig>;
}

export interface LayoutInput {
  /** Visible viewport in CSS px (visualViewport). */
  vw: number;
  vh: number;
  insets: Insets;
  cols: number;
  rows: number;
  /** World cell of the board's top-left cell (the mask's x0, y0); default (0, 0). */
  origin?: Cell;
}

export interface Layout {
  cls: LayoutClass;
  cellCss: number;
  /** True when even the last class gives cells below `cellMin` (show the one-time full-screen note). */
  note: boolean;
  /**
   * True when cells are below `cellFloor` (44). v0.1 has no Tight class, so 5×7 below Hs 508 lands here:
   * M1 decision (CLAUDE.md) — the 44 px bar starts at Hs 508 and smaller viewports get the one-time A2HS
   * note (`note` is true for them too). Tiny viewports (split screen, landscape) as well; the UI can show
   * the RotateOverlay / "viewport too small" state (M7).
   */
  belowFloor: boolean;
  /** Content width W and safe height Hs (§2.6). */
  W: number;
  Hs: number;
  cols: number;
  rows: number;
  /** World cell shown at the board's top-left (the expansion mask's x0, y0). */
  origin: Cell;
  /** The centred content column (≤ maxWidth) inside the safe area. */
  column: Rect;
  hud: Rect;
  chip: Rect;
  /** Area between the chip and the counter (board is bottom-aligned inside it). */
  boardRegion: Rect;
  /** The cells only (cols·cell × rows·cell), client CSS px. */
  board: Rect;
  counter: Rect;
  tabs: Rect;
}

const CLASS_ORDER: readonly LayoutClass[] = ['regular', 'compact'];

function chromeOf(c: ClassConfig): number {
  return c.hud + c.chip + c.gapTop + c.gapBottom + c.counter + c.tabs;
}

function cellFor(
  W: number,
  Hs: number,
  c: ClassConfig,
  cfg: LayoutConfig,
  cols: number,
  rows: number,
): number {
  const byW = Math.floor((W - 2 * cfg.gutter) / cols);
  const byH = Math.floor((Hs - chromeOf(c) - 2 * cfg.frame) / rows);
  // ≥ 1 so a tiny viewport never yields a zero/negative board (clientToCell would divide by ≤ 0).
  return Math.max(1, Math.min(cfg.cellMax, byW, byH));
}

/** Picks the class by Hs, demoting while cells are below `cellMin` (§2.6). */
export function computeLayout(input: LayoutInput, cfg: LayoutConfig): Layout {
  const { vw, vh, insets, cols, rows } = input;
  const origin: Cell = input.origin ? { x: input.origin.x, y: input.origin.y } : { x: 0, y: 0 };
  const safeW = Math.max(0, vw - insets.left - insets.right);
  const W = Math.min(safeW, cfg.maxWidth);
  const Hs = Math.max(0, vh - insets.top - insets.bottom);

  let idx = CLASS_ORDER.findIndex((k) => Hs >= cfg.classes[k].minHs);
  if (idx < 0) idx = CLASS_ORDER.length - 1;
  let cls = CLASS_ORDER[idx] as LayoutClass;
  let cell = cellFor(W, Hs, cfg.classes[cls], cfg, cols, rows);
  while (cell < cfg.cellMin && idx < CLASS_ORDER.length - 1) {
    idx += 1;
    cls = CLASS_ORDER[idx] as LayoutClass;
    cell = cellFor(W, Hs, cfg.classes[cls], cfg, cols, rows);
  }
  const note = cell < cfg.cellMin;
  const c = cfg.classes[cls];

  const column: Rect = { x: insets.left + (safeW - W) / 2, y: insets.top, w: W, h: Hs };
  const hud: Rect = { x: column.x, y: column.y, w: W, h: c.hud };
  const chip: Rect = { x: column.x, y: hud.y + hud.h, w: W, h: c.chip };
  const tabs: Rect = { x: column.x, y: column.y + Hs - c.tabs, w: W, h: c.tabs };
  const counter: Rect = { x: column.x, y: tabs.y - c.counter, w: W, h: c.counter };
  const regionTop = chip.y + chip.h + c.gapTop;
  const regionBottom = counter.y - c.gapBottom;
  const boardRegion: Rect = { x: column.x, y: regionTop, w: W, h: regionBottom - regionTop };
  const bw = cols * cell;
  const bh = rows * cell;
  const board: Rect = {
    x: column.x + (W - bw) / 2,
    y: regionBottom - cfg.frame - bh,
    w: bw,
    h: bh,
  };
  return {
    cls,
    cellCss: cell,
    note,
    belowFloor: cell < cfg.cellFloor,
    W,
    Hs,
    cols,
    rows,
    origin,
    column,
    hud,
    chip,
    boardRegion,
    board,
    counter,
    tabs,
  };
}

/** Client CSS px of a world cell's centre (layout.cellToClient). */
export function cellCenter(l: Layout, x: number, y: number): { x: number; y: number } {
  return {
    x: l.board.x + (x - l.origin.x + 0.5) * l.cellCss,
    y: l.board.y + (y - l.origin.y + 0.5) * l.cellCss,
  };
}

/**
 * Client CSS px → FRACTIONAL world-cell coordinates, unbounded (cell (x, y) spans [x, x+1) × [y, y+1)), or
 * null on a degenerate layout: the one inverse of `cellCenter` that every client → cell mapping derives from
 * (§2.6: board cells, world cells incl. the rock around the mask, drag hysteresis).
 */
export function clientToCellF(l: Layout, cx: number, cy: number): { x: number; y: number } | null {
  if (!(l.cellCss > 0)) return null;
  return {
    x: (cx - l.board.x) / l.cellCss + l.origin.x,
    y: (cy - l.board.y) / l.cellCss + l.origin.y,
  };
}

/** Client CSS px → world cell on the board, or null outside the board (layout.clientToCell). */
export function clientToCell(l: Layout, cx: number, cy: number): Cell | null {
  const f = clientToCellF(l, cx, cy);
  if (f === null) return null;
  const x = Math.floor(f.x);
  const y = Math.floor(f.y);
  if (x < l.origin.x || y < l.origin.y || x >= l.origin.x + l.cols || y >= l.origin.y + l.rows)
    return null;
  return { x, y };
}

/**
 * Client CSS px → world cell anywhere on the fixed `worldCols × worldRows` grid (the rock around the
 * expansion mask included), or null outside it (hitTest: a rock cell opens its Expansion card).
 */
export function clientToWorldCell(
  l: Layout,
  cx: number,
  cy: number,
  worldCols: number,
  worldRows: number,
): Cell | null {
  const f = clientToCellF(l, cx, cy);
  if (f === null) return null;
  const x = Math.floor(f.x);
  const y = Math.floor(f.y);
  if (x < 0 || y < 0 || x >= worldCols || y >= worldRows) return null;
  return { x, y };
}

export function rectContains(r: Rect, x: number, y: number): boolean {
  return x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
}
